import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import {
  getAuthToken,
  setAuthToken,
  setRefreshToken,
  clearAllTokens,
  getProfile,
  logoutUser,
  getRolesFromToken,
  SESSION_EXPIRED_EVENT,
} from '../api/services/authService';

const AuthContext = createContext(null);

/**
 * Provides authentication state to the entire app.
 * On mount, checks for a stored token and fetches the user profile to populate user/roles.
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Populate auth state from a stored token (fetches profile).
  const loadUserFromToken = useCallback(async () => {
    const token = getAuthToken();
    if (!token) {
      setUser(null);
      setRoles([]);
      setIsAuthenticated(false);
      setLoading(false);
      return;
    }

    try {
      // Optimistically set roles from the JWT so route guards work immediately,
      // then refresh from the profile response.
      setRoles(getRolesFromToken());
      const profileRes = await getProfile();
      const data = profileRes?.data || profileRes;
      setUser(data || null);
      const profileRoles = data?.roles || data?.userRoles || getRolesFromToken();
      setRoles(
        Array.isArray(profileRoles) ? profileRoles.map((r) => String(r).toUpperCase()) : []
      );
      setIsAuthenticated(true);
    } catch (error) {
      // Profile fetch failed — clear auth state. If it was an auth error, tokens are
      // already cleared by authService; otherwise keep the token but mark unauthenticated.
      if (error?.isAuthError || error?.status === 401 || error?.status === 403) {
        clearAllTokens();
      }
      setUser(null);
      setRoles([]);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  }, []);

  // On mount, attempt to restore the session from a stored token.
  useEffect(() => {
    loadUserFromToken();
  }, [loadUserFromToken]);

  // Listen for session-expiry events dispatched by authService (e.g. refresh failed).
  useEffect(() => {
    const handleSessionExpired = () => {
      setUser(null);
      setRoles([]);
      setIsAuthenticated(false);
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
  }, []);

  /**
   * Store tokens and populate auth state.
   * @param {string} token - Access token
   * @param {string} refreshToken - Refresh token
   * @param {Object} [userData] - Optional user object
   * @param {string[]} [userRoles] - Optional roles array
   */
  const login = useCallback((token, refreshToken, userData = null, userRoles = null) => {
    if (token) setAuthToken(token);
    if (refreshToken) setRefreshToken(refreshToken);
    if (userData) setUser(userData);
    if (userRoles && Array.isArray(userRoles)) {
      setRoles(userRoles.map((r) => String(r).toUpperCase()));
    } else {
      setRoles(getRolesFromToken());
    }
    setIsAuthenticated(true);
    setLoading(false);
  }, []);

  /**
   * Log out: revoke refresh token server-side and clear state.
   */
  const logout = useCallback(async () => {
    await logoutUser();
    setUser(null);
    setRoles([]);
    setIsAuthenticated(false);
  }, []);

  /**
   * Re-fetch the user profile and update state.
   */
  const refreshUser = useCallback(async () => {
    const token = getAuthToken();
    if (!token) return;
    try {
      const profileRes = await getProfile();
      const data = profileRes?.data || profileRes;
      setUser(data || null);
      const profileRoles = data?.roles || data?.userRoles || getRolesFromToken();
      setRoles(
        Array.isArray(profileRoles) ? profileRoles.map((r) => String(r).toUpperCase()) : []
      );
      setIsAuthenticated(true);
    } catch (error) {
      if (error?.isAuthError || error?.status === 401 || error?.status === 403) {
        clearAllTokens();
        setUser(null);
        setRoles([]);
        setIsAuthenticated(false);
      }
    }
  }, []);

  const value = useMemo(
    () => ({
      isAuthenticated,
      user,
      roles,
      loading,
      login,
      logout,
      refreshUser,
    }),
    [isAuthenticated, user, roles, loading, login, logout, refreshUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Hook to access the auth context.
 * @returns {{isAuthenticated: boolean, user: Object|null, roles: string[], loading: boolean, login: Function, logout: Function, refreshUser: Function}}
 */
export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an <AuthProvider>');
  }
  return ctx;
};

export default AuthContext;
